import { Injectable } from '@angular/core';
import { Subject, firstValueFrom, map } from 'rxjs';
import { HttpService } from './http.service';
import { EndpointsService } from './endpoints.service';
import { ConfirmController } from '../controllers/confirm.controller';

@Injectable({
    providedIn: 'root',
})
export class BusinessesService {
    private _page: number = 0;
    private _filtersSource = new Subject<any>();
    private _filters = {
        search: '',
        category: '',
        position: '',
        page: 0,
    };

    filters$ = this._filtersSource.asObservable();

    set filters(filters: any) {
        this._filters = {
            ...this._filters,
            ...filters,
        };
        this._filtersSource.next(this._filters);
    }

    get filters() {
        return this._filters;
    }

    set page(value: number) {
        if (value < 0) {
            this._page = 0;
        } else {
            this._page = value;
        }
    }

    get page() {
        return this._page;
    }

    constructor(
        private confirmController: ConfirmController,
        private httpService: HttpService,
        private endpoints: EndpointsService
    ) {}

    fetch(filters?: any) {
        const body = {
            position: (filters && filters['position']) || '',
            businessTypeId: (filters && filters['category']) || '',
            predictive: (filters && filters['search']) || '',
            page: (filters && filters['page']) || 0,
            size: '20',
        };

        return this.httpService.post(this.endpoints.businesses, body).pipe(
            // Filter out the businesses having the flag "businessApplication" false
            map((response: any) => {
                const data = response.data;
                const filteredData = data.filter(
                    (business: any) => business.businessApplication
                );
                response.data = filteredData;
                response.data = response?.data?.map((business: any) => {
                    if (business.color === '' || business.color === null) {
                        business.color = 'primary';
                    }
                    business.businessIdPraga = business.businessId ?? '';
                    return business;
                });
                response.data = response?.data?.map((business: any) => {
                    if (
                        business.hasDelivery &&
                        filters &&
                        filters['position']
                    ) {
                        try {
                            const businessPosition = {
                                lat: Number(business.location.split(' ')[0]),
                                lon: Number(business.location.split(' ')[1]),
                            };
                            const actualPosition = {
                                lat: Number(filters['position'].split(' ')[0]),
                                lon: Number(filters['position'].split(' ')[1]),
                            };
                            business.canDeliverToActualPosition =
                                BusinessesService.canDeliverToActualPosition(
                                    businessPosition,
                                    actualPosition,
                                    business.deliveryrange
                                );
                        } catch (error) {
                            console.error(
                                'could not calculate canDeliverToActualPosition'
                            );
                        }
                    }
                    return business;
                });
                return response.data;
            })
        );
    }

    nextPage() {
        this.page++;
        this.filters = {
            page: this.page,
        };
    }

    setPage(page: number) {
        this.filters = {
            page: page,
        };
    }

    resetFilters() {
        this._filters = {
            search: '',
            category: '',
            position: '',
            page: 0,
        };
    }

    async registerQualify(
        ownerId: any,
        businessId: any,
        sale: any,
        score: any,
        obj: any
    ) {
        const response: any = await firstValueFrom(
            this.httpService.put(
                this.endpoints.qualify
                    .replace('{ownerId}', ownerId)
                    .replace('{businessId}', businessId)
                    .replace('{saleId}', sale)
                    .replace('{scoreId}', score),
                obj
            )
        );
        if (response && response.success) {
            if (response.success) {
                return response.data;
            } else {
                await this.confirmController.createAndPresent({
                    message: response.message,
                });
            }
        }
    }

    async obtieneLogoQualify(ownerId: any, businessId: any) {
        const response: any = await firstValueFrom(
            this.httpService.get(
                this.endpoints.scoreImage
                    .replace('{ownerId}', ownerId)
                    .replace('{businessId}', businessId)
            )
        );

        if (response && response.success) {
            if (response.success) {
                return response.data;
            } else {
                await this.confirmController.createAndPresent({
                    message: response.message,
                });
            }
        }
    }

    public static canDeliverToActualPosition(
        businnesPosition: { lat: number; lon: number },
        actualPosition: { lat: number; lon: number },
        maxDistanceBusinessDelivery: number
    ): boolean {
        try {
            const distance = BusinessesService.calcularDistancia(
                businnesPosition.lat,
                businnesPosition.lon,
                actualPosition.lat,
                actualPosition.lon
            );
            return distance <= maxDistanceBusinessDelivery;
        } catch (error) {
            return false;
        }
    }

    public static calcularDistancia(
        lat1: number,
        lon1: number,
        lat2: number,
        lon2: number
    ): number {
        const R = 6371; // Radio de la Tierra en kilómetros
        const dLat = BusinessesService.gradosARadianes(lat2 - lat1);
        const dLon = BusinessesService.gradosARadianes(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(BusinessesService.gradosARadianes(lat1)) *
                Math.cos(BusinessesService.gradosARadianes(lat2)) *
                Math.sin(dLon / 2) *
                Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distancia = R * c; // Distancia en kilómetros

        return distancia;
    }

    public static gradosARadianes(grados: number): number {
        return grados * (Math.PI / 180);
    }
}
